import { type ISocketClient } from '@finsight/socket-client';
import SocketDITypes from '@finsight/socket-client/DITypes';
import { inject, injectable } from 'inversify';
import { type IDispatcher } from '@creditflowresearch/json-rpc-dispatcher/dispatcher-cfr';
import DITypes from '@creditflowresearch/json-rpc-dispatcher/dispatcher-cfr/DITypes';
import { Subscription } from '@dealroadshow/socket-frontend-sdk';
import { v4 as uuid } from 'uuid';
import Request from '@framework/api/Rpc/Request';
import { RpcError, RpcSuccess } from '@dealroadshow/json-rpc-dispatcher';

@injectable()
class FileDownloadLinkGenerator {
  private socketSubscriptions: Record<string, Subscription> = {};

  constructor(
    @inject(SocketDITypes.SocketClient) private readonly socket: ISocketClient,
    @inject(DITypes.JsonRpcDispatcher) private readonly rpc: IDispatcher,
  ) {}

  generateUniqueId(): string {
    return uuid();
  }

  private async subscribe(entityId: string) {
    this.socketSubscriptions[entityId] = await this.socket.subscribe(
      new Request('finsight_web.socket.notifications', { entityId }),
    );
  }

  private unsubscribe(entityId: string) {
    const subscription = this.socketSubscriptions[entityId];

    if (subscription instanceof Subscription) {
      subscription.cancel();
    }

    delete this.socketSubscriptions[entityId];
  }

  async getFileLink(
    request: Request,
    entityId: string,
    callback?: (progress: number, timeLeft: number) => void,
  ): Promise<string> {
    await this.subscribe(entityId);

    return new Promise<string>((resolve, reject) => {
      const handler = (event: any) => {
        const payload = event?.params?.payload;
        if (!payload) return;

        const { status, data = {}, error } = payload;

        if (status) {
          if (status === 'SUCCESS') {
            resolve(data.url ?? data.downloadUrl ?? '');
            return;
          } else {
            reject(new RpcError({ id: request.id, error }));
            return;
          }
        }

        if (callback) {
          if (data.estimatedTime) {
            callback(0, data.estimatedTime);
            return;
          }

          if (data.progress) {
            callback(data.progress, data.timeLeft);
          }
        }
      };

      const subscription = this.socketSubscriptions[entityId];

      subscription.on('finsight_web.export_generation', handler);

      this.rpc.call<RpcSuccess>(request);
    }).finally(() => this.unsubscribe(entityId));
  }
}

export default FileDownloadLinkGenerator;
